// utils/analytics.ts
// ✅ FULL FILE — with TEMP debug logs for college_clicks insert
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { supabase } from './supabase';

const SESSION_ID_KEY = 'user_session_id';
const VISIT_START_TIME_KEY = 'visit_start_time';

// ✅ Only enable gtag/GA4 events on real production domain
const PROD_WEB_HOSTS = new Set(['kursiaii.com', 'www.kursiaii.com']);

// ✅ Internal flag to avoid spamming console if a table is missing
let _siteVisitsDisabled = false;

// Temporary safeguard: production has environments without site_visits table applied yet.
const SITE_VISITS_TRACKING_ENABLED = false;

declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params?: Record<string, any>) => void;
    __KURSI_DISABLE_ANALYTICS__?: boolean;
  }
}

function isProdWebHost(): boolean {
  if (Platform.OS !== 'web') return false;
  if (typeof window === 'undefined') return false;
  const host = window.location?.hostname || '';
  return PROD_WEB_HOSTS.has(host);
}

// Controls gtag/GA4 events – production only to avoid polluting analytics dashboards
function shouldTrack(): boolean {
  if (Platform.OS !== 'web') return true;
  if (typeof window !== 'undefined' && window.__KURSI_DISABLE_ANALYTICS__ === true) return false;
  return isProdWebHost();
}

// Controls Supabase DB inserts – works on any domain so click rows are always created
function shouldTrackDB(): boolean {
  if (typeof window !== 'undefined' && window.__KURSI_DISABLE_ANALYTICS__ === true) return false;
  return true;
}

function isGtagAvailable(): boolean {
  return Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.gtag === 'function';
}

function trackGtagEvent(eventName: string, params?: Record<string, any>): void {
  if (!shouldTrack()) return;

  if (isGtagAvailable() && window.gtag) {
    try {
      window.gtag('event', eventName, params);
    } catch {
      // keep quiet
    }
  }
}

async function getOrCreateSessionId(): Promise<string> {
  try {
    let sessionId = await AsyncStorage.getItem(SESSION_ID_KEY);

    if (!sessionId) {
      const uuid =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

      sessionId = `session_${uuid}`;
      await AsyncStorage.setItem(SESSION_ID_KEY, sessionId);
    }

    return sessionId;
  } catch {
    const uuid =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

    return `session_${uuid}`;
  }
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' | 'unknown' {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }
  if (Platform.OS === 'ios' || Platform.OS === 'android') return 'mobile';
  return 'unknown';
}

function getReferrer(): string | null {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    return document.referrer || null;
  }
  return null;
}

function getUserAgent(): string | null {
  if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
    return navigator.userAgent || null;
  }
  return Platform.OS === 'ios' ? 'iOS App' : Platform.OS === 'android' ? 'Android App' : null;
}

// ─── Topic clicks ─────────────────────────────────────────────────────────────

export async function trackTopicClick(topic: string): Promise<void> {
  if (!shouldTrackDB()) return;

  try {
    const sessionId = await getOrCreateSessionId();

    const { error } = await supabase.from('topic_clicks').insert({
      topic,
      user_session_id: sessionId,
      clicked_at: new Date().toISOString(),
    });

    if (error) {
      // TEMP debug (optional)
      // console.log('topic_clicks insert error:', error);
      return;
    }
  } catch {
    // quiet
  }
}

// ─── College clicks ────────────────────────────────────────────────────────────

export async function trackCollegeClick(collegeName: string, collegeUrl?: string): Promise<void> {
  if (!shouldTrackDB()) return;

  // ✅ TEMP DEBUG: verify this function is actually firing
  console.log('trackCollegeClick fired', { collegeName, collegeUrl });

  try {
    const sessionId = await getOrCreateSessionId();
    const deviceType = getDeviceType();

    const { error } = await supabase.from('college_clicks').insert({
      college_name: collegeName,
      college_url: collegeUrl ?? null,
      user_session_id: sessionId,
      device_type: deviceType,
      clicked_at: new Date().toISOString(),
    });

    if (error) {
      // ✅ TEMP DEBUG: show the real reason (RLS / wrong project / missing table etc.)
      console.log('college_clicks insert error:', error);

      const msg = String((error as any)?.message || '');
      if (
        !msg.includes('Could not find the table') &&
        !msg.includes('college_clicks') &&
        (error as any)?.code !== 'PGRST205'
      ) {
        // keep quiet
      }
      return;
    }

    trackGtagEvent('college_click', {
      college_name: collegeName,
      college_url: collegeUrl,
    });
  } catch (e) {
    // ✅ TEMP DEBUG: catch-level failures
    console.log('trackCollegeClick exception:', e);
  }
}

// ─── Search ───────────────────────────────────────────────────────────────────

export function trackSearchCourse(searchTerm: string): void {
  trackGtagEvent('search_course', { search_term: searchTerm });
}

// ─── Chat ─────────────────────────────────────────────────────────────────────

export function trackChatStart(): void {
  trackGtagEvent('chat_start');
}

// ─── Course click ─────────────────────────────────────────────────────────────

export interface CourseClickParams {
  course_id?: string;
  course_name?: string;
  course_type?: string;
  mentor_name?: string;
  has_voucher?: boolean;
  source?: string;
}

export function trackCourseClick(paramsOrName: CourseClickParams | string, provider?: string): void {
  if (typeof paramsOrName === 'string') {
    trackGtagEvent('course_click', {
      course_name: paramsOrName,
      provider: provider || 'unknown',
    });
    return;
  }

  trackGtagEvent('course_click', {
    course_id: paramsOrName.course_id,
    course_name: paramsOrName.course_name,
    course_type: paramsOrName.course_type,
    mentor_name: paramsOrName.mentor_name,
    has_voucher: paramsOrName.has_voucher,
    source: paramsOrName.source,
  });
}

// ─── Lead / form ──────────────────────────────────────────────────────────────

export function trackLeadSubmit(email?: string): void {
  trackGtagEvent('lead_submit', { email: email ? 'provided' : 'not_provided' });
}

export function trackMentorJoinClick(): void {
  trackGtagEvent('mentor_join_click');
}

// ─── Page views ───────────────────────────────────────────────────────────────

export async function trackSiteVisit(pagePath: string): Promise<void> {
  if (!shouldTrack()) return;

  const deviceType = getDeviceType();
  trackGtagEvent('page_view', { page_path: pagePath, device_type: deviceType });

  if (!SITE_VISITS_TRACKING_ENABLED) {
    return;
  }

  if (_siteVisitsDisabled) return;

  try {
    const sessionId = await getOrCreateSessionId();
    const referrer = getReferrer();
    const userAgent = getUserAgent();

    await AsyncStorage.setItem(VISIT_START_TIME_KEY, Date.now().toString());

    const { error } = await supabase.from('site_visits').insert({
      user_session_id: sessionId,
      page_path: pagePath,
      referrer,
      user_agent: userAgent,
      device_type: deviceType,
      visited_at: new Date().toISOString(),
    });

    if (error) {
      const code = (error as any)?.code;
      const msg = String((error as any)?.message || '');

      if (code === 'PGRST205' || msg.includes('Could not find the table') || msg.includes('site_visits')) {
        _siteVisitsDisabled = true;
      }
      return;
    }
  } catch {
    // quiet
  }
}

export async function trackPageView(pagePath: string): Promise<void> {
  await trackSiteVisit(pagePath);
}

export async function trackPageLeave(pagePath: string): Promise<void> {
  if (!shouldTrack()) return;

  try {
    const startTimeStr = await AsyncStorage.getItem(VISIT_START_TIME_KEY);
    if (!startTimeStr) return;

    const startTime = parseInt(startTimeStr, 10);
    const durationSeconds = Math.floor((Date.now() - startTime) / 1000);

    void durationSeconds;
    void pagePath;
  } catch {
    // quiet
  }
}

// ─── Chat session ─────────────────────────────────────────────────────────────

export async function trackChatSessionStart(params: {
  firstMessage?: string;
  sourcePage: string;
  initialTopic?: string;
  conversationId?: string;
}): Promise<void> {
  if (!shouldTrack()) return;

  try {
    const sessionId = await getOrCreateSessionId();
    const referrer = getReferrer();

    const { error } = await supabase.from('chat_analytics_sessions').insert({
      user_session_id: sessionId,
      conversation_id: params.conversationId || null,
      started_at: new Date().toISOString(),
      first_message: params.firstMessage || null,
      source_page: params.sourcePage,
      initial_topic: params.initialTopic || null,
      referrer,
    });

    if (error) return;

    trackGtagEvent('chat_start', {
      source_page: params.sourcePage,
      has_initial_topic: !!params.initialTopic,
      has_first_message: !!params.firstMessage,
    });
  } catch {
    // quiet
  }
}

// ─── Button click ─────────────────────────────────────────────────────────────

export async function trackButtonClick(
  buttonName: string,
  pagePath: string,
  extraParams?: Record<string, any>,
): Promise<void> {
  if (!shouldTrack()) return;

  try {
    const sessionId = await getOrCreateSessionId();

    trackGtagEvent('button_click', {
      button_name: buttonName,
      page_path: pagePath,
      session_id: sessionId,
      ...extraParams,
    });

    if (extraParams && Object.keys(extraParams).length > 0) {
      const { error } = await supabase.from('button_clicks').insert({
        button_name: buttonName,
        page_path: pagePath,
        user_session_id: sessionId,
        extra_params: extraParams,
        clicked_at: new Date().toISOString(),
      });

      if (error) {
        const msg = String((error as any)?.message || '');
        if (
          !msg.includes('Could not find the table') &&
          !msg.includes('button_clicks') &&
          (error as any)?.code !== 'PGRST205'
        ) {
          // keep quiet
        }
      }
    }
  } catch {
    // quiet
  }
}

// ─── Form submit ──────────────────────────────────────────────────────────────

export async function trackFormSubmit(formName: string, success: boolean): Promise<void> {
  if (!shouldTrack()) return;

  try {
    trackGtagEvent('form_submit', { form_name: formName, success });
  } catch {
    // quiet
  }
}

// ─── Error tracking ───────────────────────────────────────────────────────────

export async function trackError(errorMessage: string, errorLocation: string): Promise<void> {
  if (!shouldTrack()) return;

  try {
    trackGtagEvent('error', {
      error_message: errorMessage,
      error_location: errorLocation,
    });
  } catch {
    // quiet
  }
}

// ─── Funnel step tracking ─────────────────────────────────────────────────────

export type FunnelStep =
  | 'landing'
  | 'topic_click'
  | 'chat_start'
  | 'course_view'
  | 'course_cta_click'
  | 'lead_modal_open'
  | 'lead_submit'
  | 'quiz_start'
  | 'quiz_complete';

export async function trackFunnelStep(step: FunnelStep, metadata?: Record<string, any>): Promise<void> {
  if (!shouldTrack()) return;

  try {
    const sessionId = await getOrCreateSessionId();

    trackGtagEvent('funnel_progress', { step, session_id: sessionId, ...metadata });

    await supabase.from('funnel_events').insert({
      session_id: sessionId,
      step,
      metadata: metadata ?? null,
      created_at: new Date().toISOString(),
    });
  } catch {
    // quiet
  }
}

// ─── Form event tracking ──────────────────────────────────────────────────────

export type FormEventType = 'focus' | 'validation_error' | 'submit_attempt' | 'submit_success' | 'submit_fail';

export async function trackFormEvent(
  formName: string,
  eventType: FormEventType,
  fieldName?: string,
  errorMessage?: string,
): Promise<void> {
  if (!shouldTrack()) return;

  try {
    const sessionId = await getOrCreateSessionId();

    trackGtagEvent('form_event', {
      form_name: formName,
      event_type: eventType,
      field_name: fieldName,
    });

    await supabase.from('form_events').insert({
      session_id: sessionId,
      form_name: formName,
      event_type: eventType,
      field_name: fieldName ?? null,
      error_message: errorMessage ?? null,
      created_at: new Date().toISOString(),
    });
  } catch {
    // quiet
  }
}

// ─── Chat flow / funnel step tracking ────────────────────────────────────────

export type ChatFlowEventType =
  | 'chat_opened'
  | 'user_message'
  | 'assistant_question'
  | 'search_triggered'
  | 'courses_shown'
  | 'lead_gate_shown'
  | 'lead_gate_skipped'
  | 'lead_submitted';

export interface ChatFlowEventParams {
  eventType: ChatFlowEventType;
  conversationId?: string | null;
  stepNumber?: number | null;
  isQuickReply?: boolean;
  messagePreview?: string | null;
  optionsCount?: number | null;
  coursesCount?: number | null;
  topic?: string | null;
}

export async function trackChatFlowEvent(params: ChatFlowEventParams): Promise<void> {
  if (!shouldTrackDB()) return;

  try {
    const sessionId = await getOrCreateSessionId();
    const deviceType = getDeviceType();

    await supabase.from('chat_flow_events').insert({
      session_id: sessionId,
      conversation_id: params.conversationId ?? null,
      event_type: params.eventType,
      step_number: params.stepNumber ?? null,
      is_quick_reply: params.isQuickReply ?? false,
      message_preview: params.messagePreview ? params.messagePreview.slice(0, 200) : null,
      options_count: params.optionsCount ?? null,
      courses_count: params.coursesCount ?? null,
      topic: params.topic ?? null,
      device_type: deviceType,
    });
  } catch {
    // quiet — never block the chat UX
  }
}

// ─── Page engagement tracking ─────────────────────────────────────────────────

export async function trackPageEngagement(
  pagePath: string,
  scrollDepthPct: number,
  timeOnPageSeconds: number,
): Promise<void> {
  if (!shouldTrack()) return;

  try {
    const sessionId = await getOrCreateSessionId();

    trackGtagEvent('page_engagement', {
      page_path: pagePath,
      scroll_depth_pct: scrollDepthPct,
      time_on_page_seconds: timeOnPageSeconds,
    });

    await supabase.from('page_engagement').insert({
      session_id: sessionId,
      page_path: pagePath,
      scroll_depth_pct: Math.min(100, Math.max(0, Math.round(scrollDepthPct))),
      time_on_page_seconds: Math.max(0, Math.round(timeOnPageSeconds)),
      created_at: new Date().toISOString(),
    });
  } catch {
    // quiet
  }
}